import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Cartao, classesBotao, Selo } from '../../componentes/base';
import { LinkExportacao } from '../../componentes/discreto';
import { Estado, Vazio } from '../../componentes/estados';
import { get, textoDeErroGravado } from '../../nucleo/api';
import { data, dataHora } from '../../nucleo/formato';
import { Rolagem, TD, TH } from './comum';
import type { Exportacao } from './tipos';

const COR = { pendente: 'neutro', pronto: 'verde', erro: 'perigo' } as const;

/** Exportações CSV do próprio usuário (job): situação, linhas e o arquivo para baixar. */
export function Exportacoes() {
  const { t } = useTranslation('relatorios');
  const q = useQuery({
    queryKey: ['/relatorios/exportacoes'],
    queryFn: () => get<{ itens: Exportacao[] }>('/relatorios/exportacoes'),
    refetchInterval: (c) => (c.state.data?.itens.some((i) => i.estado === 'pendente') ? 1500 : false),
  });
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-tinta-3">{t('exportacoes.ajuda')}</p>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Cartao>
              <Vazio icone={<Download className="size-5" aria-hidden />} titulo={t('exportacoes.vazioTitulo')} texto={t('exportacoes.vazioTexto')} />
            </Cartao>
          ) : (
            <Cartao semPadding>
              <Rolagem rotulo={t('comum.tabela')}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className={TH}>{t('exportacoes.relatorio')}</th>
                      <th scope="col" className={TH}>{t('exportacoes.pedidoEm')}</th>
                      <th scope="col" className={TH}>{t('exportacoes.estado')}</th>
                      <th scope="col" className={`${TH} text-right`}>{t('exportacoes.linhas')}</th>
                      <th scope="col" className={TH}>
                        <span className="sr-only">{t('exportacoes.baixar')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.itens.map((x) => (
                      <tr key={x.id} className="border-b border-borda last:border-0">
                        <th scope="row" className={`${TD} text-left font-normal`}>
                          {x.salvo_nome ?? t(`exportacoes.nomes.${x.relatorio}`)}
                          {(x.mascarado || x.agendada) && (
                            <span className="mt-1 flex flex-wrap gap-1">
                              {x.mascarado && <Selo>{t('exportacoes.mascarada')}</Selo>}
                              {x.agendada && <Selo>{t('exportacoes.agendada')}</Selo>}
                            </span>
                          )}
                          {typeof x.parametros.de === 'string' && <span className="block text-xs text-tinta-3">{t('fluxo.intervalo', { de: data(String(x.parametros.de)), ate: data(String(x.parametros.ate ?? x.parametros.de)) })}</span>}
                        </th>
                        <td className={`${TD} numero whitespace-nowrap text-tinta-2`}>{dataHora(x.created_at)}</td>
                        <td className={TD}>
                          <Selo cor={COR[x.estado]}>{t(`exportacoes.estados.${x.estado}`)}</Selo>
                          {x.erro && <span className="mt-1 block text-xs text-perigo">{textoDeErroGravado(x.erro)}</span>}
                        </td>
                        <td className={`${TD} numero text-right`}>{x.linhas ?? ''}</td>
                        <td className={`${TD} text-right`}>
                          {x.estado === 'pronto' && (
                            <LinkExportacao href={`/api/v1/relatorios/exportacoes/${x.id}/arquivo`} download className={classesBotao('secundario', 'sm')}>
                              <Download className="size-4" aria-hidden />
                              {t('exportacoes.baixar')}
                            </LinkExportacao>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Rolagem>
            </Cartao>
          )
        }
      </Estado>
    </div>
  );
}
